import { useState } from 'react';
import {
  CardContent,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { ManualApplicationEntity } from '@shared/api';
import { useRouteHook } from '@shared/hooks';
import { DateUtils, RoutePaths } from '@shared/utils';

const COLUMNS_COUNT = 5;

interface ManualApplicationsTableProps {
  loading: boolean;
  data: ManualApplicationEntity[];
}

export const ManualApplicationsTable = (props: ManualApplicationsTableProps) => {
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [selectedRow, setSelectedRow] = useState<ManualApplicationEntity | null>(null);

  const { navigate } = useRouteHook();

  const handleOpenMenu = (e: React.MouseEvent<HTMLButtonElement>, row: ManualApplicationEntity) => {
    setMenuAnchor(e.currentTarget);
    setSelectedRow(row);
  };

  const handleCloseMenu = () => {
    setMenuAnchor(null);
    setSelectedRow(null);
  };

  const routeToManualApplication = (manualAppId: string, partnerUserId: string) => {
    navigate(RoutePaths.manualApplicationsDetails.onNavigate(manualAppId), { state: { partnerUserId } });
    handleCloseMenu();
  };

  if (props.loading) {
    return (
      <CardContent style={{ textAlign: 'center' }}>
        <Typography variant='body1'>Loading application details</Typography>
      </CardContent>
    );
  }
  if (!props.data) {
    return (
      <CardContent style={{ textAlign: 'center' }}>
        <Typography align='center'>
          No data to display.
          <br />
          Start by performing a search.
        </Typography>
      </CardContent>
    );
  }
  return (
    <>
      <Table aria-label='simple table' stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Reference</TableCell>
            <TableCell style={{ width: '200px' }} align='center'>
              Partner
            </TableCell>
            <TableCell style={{ width: '200px' }} align='center'>
              Status
            </TableCell>
            <TableCell style={{ width: '200px' }} align='center'>
              Last Updated
            </TableCell>
            <TableCell style={{ width: '100px' }} align='center'>
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {props.data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
            <TableRow key={row.manualAppId} hover>
              <TableCell component='th' scope='row'>
                <Link onClick={() => routeToManualApplication(row.manualAppId, row.userId)} color='primary' style={{ cursor: 'pointer' }}>
                  {row.manRefId || row.manualAppId}
                </Link>
                <br />
              </TableCell>
              <TableCell align='center'>{row.partnerName}</TableCell>
              <TableCell align='center'>{row.status}</TableCell>
              <TableCell align='center'>{DateUtils.formatDate(row.lastUpdated)}</TableCell>
              <TableCell align='center'>
                <IconButton onClick={(e) => handleOpenMenu(e, row)} size='large'>
                  <MoreVertIcon />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
          {!props.data.length && (
            <TableRow>
              <TableCell colSpan={COLUMNS_COUNT} align='center'>
                No Records Found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleCloseMenu}>
        {selectedRow && <MenuItem onClick={() => routeToManualApplication(selectedRow.manualAppId, selectedRow.userId)}>View</MenuItem>}
      </Menu>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component='div'
        count={props.data.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(_, value) => setPage(value)}
        onRowsPerPageChange={(e) => {
          setPage(0);
          setRowsPerPage(+e.target.value);
        }}
      />
    </>
  );
};
